import { useEffect, type RefObject } from "react";

type Star = {
  x: number; y: number; r: number; a: number; tw: number; ph: number;
  hue: "warm" | "cool";
};
type Dust = {
  x: number; y: number; vx: number; vy: number; r: number; a: number;
};
type Mote = {
  x: number; y: number; vx: number; vy: number; r: number; a: number; ph: number;
};
type Sparkle = {
  x: number; y: number; life: number; decay: number; r: number;
};
type Meteor = {
  x: number; y: number; vx: number; vy: number;
  life: number; decay: number; len: number;
};

type AccentColors = {
  accent: [number, number, number];
  accent2: [number, number, number];
};

const LITE_MQ = "(max-width: 768px)";

function isLiteAtmosphere(): boolean {
  return window.matchMedia(LITE_MQ).matches;
}

function parseHexRgb(hex: string): [number, number, number] | null {
  const h = hex.trim().replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return [r, g, b];
}

function readAccentColors(): AccentColors {
  const style = getComputedStyle(document.documentElement);
  const accent = parseHexRgb(style.getPropertyValue("--accent")) ?? [242, 92, 31];
  const accent2 = parseHexRgb(style.getPropertyValue("--accent-2")) ?? [255, 138, 61];
  return { accent, accent2 };
}

function mixRgb(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

const IVORY: [number, number, number] = [245, 239, 226];

/**
 * Starfield v2 — twinkling stars, rising dust, drifting motes, random
 * sparkles, and rare shooting stars. Particle colors follow --accent vars.
 * Lite profile on narrow viewports: fewer particles, simpler draws, paused
 * when the tab is hidden.
 */
export function useStarfield(canvasRef: RefObject<HTMLCanvasElement>): void {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let lite = isLiteAtmosphere();
    let DPR = Math.min(window.devicePixelRatio || 1, lite ? 1.25 : 2);
    let W = 0, H = 0;
    let stars: Star[] = [];
    let dust: Dust[] = [];
    let motes: Mote[] = [];
    let sparkles: Sparkle[] = [];
    let meteors: Meteor[] = [];
    let nextMeteorAt = 0;
    let nextSparkleAt = 0;
    let colors = readAccentColors();
    let raf = 0;
    let stopped = false;
    let visible = document.visibilityState !== "hidden";

    function resize() {
      lite = isLiteAtmosphere();
      DPR = Math.min(window.devicePixelRatio || 1, lite ? 1.25 : 2);
      const rect = canvas!.getBoundingClientRect();
      W = rect.width; H = rect.height;
      canvas!.width = W * DPR;
      canvas!.height = H * DPR;
      ctx!.setTransform(DPR, 0, 0, DPR, 0, 0);
    }

    function seed() {
      colors = readAccentColors();
      lite = isLiteAtmosphere();
      const density = lite ? 0.48 : 1;

      stars = [];
      const count = Math.round((W * H) / (lite ? 11000 : 5800) * density);
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          r: Math.random() * 1.4 + 0.15,
          a: Math.random() * 0.75 + 0.15,
          tw: Math.random() * 0.028 + 0.003,
          ph: Math.random() * Math.PI * 2,
          hue: Math.random() > (lite ? 0.94 : 0.88) ? "warm" : "cool",
        });
      }

      dust = [];
      const dustCount = Math.round((lite ? 14 : 28) + (W * H) / (lite ? 72000 : 42000));
      for (let i = 0; i < dustCount; i++) {
        const drift = Math.random();
        dust.push({
          x: Math.random() * W,
          y: Math.random() * H,
          vx: (Math.random() - 0.5) * (drift > 0.7 ? 0.22 : 0.1),
          vy: -Math.random() * 0.18 - 0.01,
          r: Math.random() * 2 + 0.4,
          a: Math.random() * 0.45 + 0.1,
        });
      }

      motes = [];
      if (!lite) {
        const moteCount = Math.round(18 + (W * H) / 55000);
        for (let i = 0; i < moteCount; i++) {
          motes.push({
            x: Math.random() * W,
            y: Math.random() * H,
            vx: (Math.random() - 0.5) * 0.14,
            vy: (Math.random() - 0.5) * 0.1,
            r: Math.random() * 0.9 + 0.25,
            a: Math.random() * 0.35 + 0.08,
            ph: Math.random() * Math.PI * 2,
          });
        }
      }

      sparkles = [];
      meteors = [];
      nextMeteorAt = performance.now() + (lite ? 5000 : 2000) + Math.random() * 4000;
      nextSparkleAt = performance.now() + (lite ? 1200 : 400) + Math.random() * 800;
    }

    function maxMeteors() {
      return lite ? 1 : 3;
    }

    function maxSparkles() {
      return lite ? 0 : 12;
    }

    function spawnMeteor() {
      const fromLeft = Math.random() > 0.5;
      const speed = 7 + Math.random() * 6;
      const ang = (fromLeft ? 0.45 : 2.69) + (Math.random() - 0.5) * 0.22;
      meteors.push({
        x: fromLeft
          ? -40 + Math.random() * W * 0.35
          : W * 0.65 + Math.random() * W * 0.35 + 40,
        y: Math.random() * H * 0.38,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed * 0.55 + 1.2,
        life: 1,
        decay: 0.01 + Math.random() * 0.01,
        len: 80 + Math.random() * 90,
      });
    }

    function spawnSparkle() {
      if (sparkles.length >= maxSparkles()) return;
      sparkles.push({
        x: Math.random() * W,
        y: Math.random() * H * 0.85,
        life: 1,
        decay: 0.025 + Math.random() * 0.035,
        r: Math.random() * 1.2 + 0.4,
      });
    }

    function frame(t: number) {
      if (stopped || !visible) return;
      ctx!.clearRect(0, 0, W, H);

      const warmStar = mixRgb(colors.accent, IVORY, 0.35);
      const coolStar = mixRgb(IVORY, [180, 195, 255] as [number, number, number], 0.25);

      for (const st of stars) {
        st.ph += st.tw;
        const a = st.a * (0.55 + 0.45 * Math.sin(st.ph));
        const [r, g, b] = st.hue === "warm" ? warmStar : coolStar;
        ctx!.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
        ctx!.beginPath();
        ctx!.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx!.fill();
      }

      const [dr, dg, db] = colors.accent;
      const [ar, ag, ab] = mixRgb(colors.accent, colors.accent2, 0.45);
      for (const p of dust) {
        p.x += p.vx + (lite ? 0 : Math.sin(t * 0.001 + p.y * 0.02) * 0.02);
        p.y += p.vy;
        if (p.y < -6) { p.y = H + 6; p.x = Math.random() * W; }
        if (p.x < -6) p.x = W + 6;
        if (p.x > W + 6) p.x = -6;
        if (lite) {
          ctx!.fillStyle = `rgba(${dr},${dg},${db},${p.a * 0.55})`;
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.r * 2.2, 0, Math.PI * 2);
          ctx!.fill();
        } else {
          const grad = ctx!.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4.5);
          grad.addColorStop(0, `rgba(${dr},${dg},${db},${p.a})`);
          grad.addColorStop(0.55, `rgba(${ar},${ag},${ab},${p.a * 0.35})`);
          grad.addColorStop(1, `rgba(${dr},${dg},${db},0)`);
          ctx!.fillStyle = grad;
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.r * 4.5, 0, Math.PI * 2);
          ctx!.fill();
        }
      }

      if (!lite) {
        const [mr, mg, mb] = mixRgb(colors.accent2, IVORY, 0.5);
        for (const m of motes) {
          m.ph += 0.018;
          m.x += m.vx + Math.cos(m.ph) * 0.04;
          m.y += m.vy + Math.sin(m.ph * 1.3) * 0.03;
          if (m.x < -8) m.x = W + 8;
          if (m.x > W + 8) m.x = -8;
          if (m.y < -8) m.y = H + 8;
          if (m.y > H + 8) m.y = -8;
          const flicker = m.a * (0.65 + 0.35 * Math.sin(m.ph * 2.1));
          ctx!.fillStyle = `rgba(${mr},${mg},${mb},${flicker})`;
          ctx!.beginPath();
          ctx!.arc(m.x, m.y, m.r, 0, Math.PI * 2);
          ctx!.fill();
        }
      }

      if (!lite && t > nextSparkleAt) {
        const burst = 1 + Math.floor(Math.random() * 3);
        for (let i = 0; i < burst; i++) spawnSparkle();
        nextSparkleAt = t + 350 + Math.random() * 900;
      }
      for (let i = sparkles.length - 1; i >= 0; i--) {
        const s = sparkles[i];
        s.life -= s.decay;
        if (s.life <= 0) { sparkles.splice(i, 1); continue; }
        const [sr, sg, sb] = mixRgb(colors.accent2, IVORY, 0.65);
        const glow = s.r * (1.8 + (1 - s.life) * 2);
        const grad = ctx!.createRadialGradient(s.x, s.y, 0, s.x, s.y, glow);
        grad.addColorStop(0, `rgba(255,252,245,${0.85 * s.life})`);
        grad.addColorStop(0.35, `rgba(${sr},${sg},${sb},${0.45 * s.life})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx!.fillStyle = grad;
        ctx!.beginPath();
        ctx!.arc(s.x, s.y, glow, 0, Math.PI * 2);
        ctx!.fill();
      }

      if (t > nextMeteorAt && meteors.length < maxMeteors()) {
        spawnMeteor();
        nextMeteorAt = t + (lite ? 12000 : 6000) + Math.random() * 8000;
      }

      const head = mixRgb(colors.accent2, IVORY, 0.55);
      const tail = mixRgb(colors.accent2, IVORY, 0.2);
      for (let i = meteors.length - 1; i >= 0; i--) {
        const m = meteors[i];
        m.x += m.vx; m.y += m.vy; m.life -= m.decay;
        if (m.life <= 0 || m.y > H * 0.65) { meteors.splice(i, 1); continue; }
        const tx = m.x - m.vx * (m.len / 8);
        const ty = m.y - m.vy * (m.len / 8);
        const grad = ctx!.createLinearGradient(m.x, m.y, tx, ty);
        grad.addColorStop(0, `rgba(${head[0]},${head[1]},${head[2]},${0.9 * m.life})`);
        grad.addColorStop(0.3, `rgba(${tail[0]},${tail[1]},${tail[2]},${0.4 * m.life})`);
        grad.addColorStop(1, `rgba(${tail[0]},${tail[1]},${tail[2]},0)`);
        ctx!.strokeStyle = grad;
        ctx!.lineWidth = 1.2 + m.life * 0.6;
        ctx!.lineCap = "round";
        ctx!.beginPath();
        ctx!.moveTo(m.x, m.y);
        ctx!.lineTo(tx, ty);
        ctx!.stroke();
        ctx!.fillStyle = `rgba(${head[0]},${head[1]},${head[2]},${m.life})`;
        ctx!.beginPath();
        ctx!.arc(m.x, m.y, 1.6, 0, Math.PI * 2);
        ctx!.fill();
      }

      raf = requestAnimationFrame(frame);
    }

    function startLoop() {
      if (stopped || !visible) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(frame);
    }

    function stopLoop() {
      cancelAnimationFrame(raf);
      raf = 0;
    }

    const onResize = () => { resize(); seed(); };

    const onVisibility = () => {
      visible = document.visibilityState !== "hidden";
      if (visible && !stopped) startLoop();
      else stopLoop();
    };

    const liteMq = window.matchMedia(LITE_MQ);
    const onLiteChange = () => { resize(); seed(); };

    resize();
    seed();
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    liteMq.addEventListener("change", onLiteChange);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduced && visible) startLoop();

    return () => {
      stopped = true;
      stopLoop();
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      liteMq.removeEventListener("change", onLiteChange);
    };
  }, [canvasRef]);
}
